/**
 * Client-side mirror of the backend `collectSlots` / `findSlotsUsingGroup`
 * utilities — kept tiny so /pages and /homepage edit pages can pre-validate
 * (e.g. count slots, find missing groupSlug) before submitting to the API.
 *
 * Re-implementing here (rather than importing from @news-cms/api) keeps the
 * web bundle independent of the worker code.
 */

import type {
  CarouselNode,
  ContainerNode,
  LayoutNode,
  PageLayout,
  SlotNode,
  SlotPreset,
} from '@news-cms/shared';

export function walkNodesClient(node: LayoutNode, visit: (n: LayoutNode) => void): void {
  visit(node);
  // Slot + Carousel + Label are leaves — no children
  if (node.type === 'slot' || node.type === 'carousel' || node.type === 'label') return;
  for (const child of node.children) walkNodesClient(child, visit);
}

export function collectSlotsClient(layout: PageLayout): SlotNode[] {
  const out: SlotNode[] = [];
  walkNodesClient(layout.root, (n) => {
    if (n.type === 'slot') out.push(n);
  });
  return out;
}

export function collectCarouselsClient(layout: PageLayout): CarouselNode[] {
  const out: CarouselNode[] = [];
  walkNodesClient(layout.root, (n) => {
    if (n.type === 'carousel') out.push(n);
  });
  return out;
}

/** Distinct groupSlug list referenced anywhere — across slots + carousels. */
export function distinctGroupSlugsClient(layout: PageLayout): string[] {
  const slugs = new Set<string>();
  walkNodesClient(layout.root, (n) => {
    if (n.type === 'slot' || n.type === 'carousel') slugs.add(n.groupSlug);
  });
  return Array.from(slugs);
}

/**
 * Normalize a legacy PageLayout so every slot/carousel has the new required
 * fields (`preset`, `itemIndex` for slots). Returns a fresh tree — the
 * input is not mutated.
 *
 * Used right before save so layouts loaded from DB without the new fields
 * pass the Zod schema. Idempotent: re-normalizing a normalized layout is
 * a no-op.
 *
 * Why this lives client-side: server-side hydrator auto-migrates only the
 * READ path. The save path goes through admin /pages PATCH which validates
 * strictly against the new schema. Without this, every legacy page would
 * fail save until manually re-edited.
 */
const VALID_PRESETS = new Set<SlotPreset>([
  'hero',
  'card',
  'featured',
  'thumb-sm',
  'thumb-md',
  'thumb-lg',
  'compact',
]);

export function normalizeLegacyLayoutClient(layout: PageLayout): PageLayout {
  const groupCursors = new Map<string, number>();

  const normalizePreset = (raw: unknown): SlotPreset => {
    if (typeof raw !== 'string') return 'card';
    if (raw === 'thumb') return 'thumb-sm';
    return VALID_PRESETS.has(raw as SlotPreset) ? (raw as SlotPreset) : 'card';
  };

  const walkChild = (node: LayoutNode): LayoutNode => {
    if (node.type === 'slot') {
      const slot = node as SlotNode & { layoutTemplateSlug?: unknown };
      const cursor = groupCursors.get(slot.groupSlug) ?? 0;
      groupCursors.set(slot.groupSlug, cursor + 1);
      const itemIndex =
        typeof slot.itemIndex === 'number' && slot.itemIndex >= 0 ? slot.itemIndex : cursor;
      return {
        id: slot.id,
        type: 'slot',
        groupSlug: slot.groupSlug,
        itemIndex,
        preset: normalizePreset(slot.preset),
      };
    }
    if (node.type === 'carousel') {
      const carousel = node;
      return {
        id: carousel.id,
        type: 'carousel',
        groupSlug: carousel.groupSlug,
        preset: normalizePreset(carousel.preset),
      };
    }
    if (node.type === 'label') {
      // No legacy form, pass-through. The Zod schema enforces preset
      // enum + text presence on save; nothing to default here.
      return node;
    }
    if (node.type === 'row') {
      return { ...node, children: node.children.map((c) => walkChild(c) as never) };
    }
    if (node.type === 'column') {
      return { ...node, children: node.children.map((c) => walkChild(c) as never) };
    }
    // container — drop legacy variant='carousel' to 'fluid' (server-side
    // migration handles the structural hoist; here we just sanitize).
    const container = node as ContainerNode & { variant: string };
    const variant: 'fluid' | 'fixed' = container.variant === 'fixed' ? 'fixed' : 'fluid';
    return {
      ...container,
      variant,
      children: container.children.map((c) => walkChild(c) as never),
    };
  };

  return {
    version: 3,
    root: walkChild(layout.root) as ContainerNode,
  };
}
